import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Divider, Tooltip} from '@mui/material';
import {DashboardPluginProps} from '../../../core/plugin/PluginType';
import Spinner from '../../../ui/Spinner';
import {TimeRecordEntry, TimeRecordStatus} from '../../../api/TimetrackingApi';
import Typography from '../../../components/Typography';
import {Chip} from '../../../components/Chip';
import {WorkDayRecordEditModal} from './WorkDayRecordEditModal';
import {DateRangeNavigator} from "../../../components/DateRangeNavigator";
import {DateUtils} from "../../../util/DateUtils";
import {useRequests} from "../../../hooks/useRequests";
import {useMe} from "../../../hooks/useMe";
import {
    TimeRecordRequest,
    TimeRecordRequestCreateRequest,
    createTimeRecordRequest,
    fetchAllTimeRecordRequests, TimeRecordEntrySnapshot,
} from "../../../api/RequestApi";
import {useWidgetActions} from "../../../core/components/useWidgetActions";
import {useTranslation} from "react-i18next";
import {Button} from "../../../components/Button";
import {useWorkdayRecords, VISIBLE_DAYS} from "../../../hooks/useWorkdayRecords";
import {Workday} from "../../../api/WorkDayRecordApi";
import {TimeScale, TimeScaleEntry} from "./TimeScale";
import {ArrowRightIcon} from "../../../components/Icons";

// ── Constants ────────────────────────────────────────────────────────────────


/** Records in these statuses show a lock indicator on the edit button. */
const NON_EDITABLE_STATUSES: TimeRecordStatus[] = [
    TimeRecordStatus.LOCKED,
    TimeRecordStatus.EXPORTED,
];

function formatMinutes(totalMinutes: number): string {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${String(m).padStart(2, '0')}m`;
}

function buildWindowDates(windowEnd: string): Date[] {
    const end = new Date(windowEnd + 'T00:00:00');
    const dates: Date[] = [];
    for (let i = VISIBLE_DAYS - 1; i >= 0; i--) {
        const d = new Date(end);
        d.setDate(d.getDate() - i);
        dates.push(d);
    }
    return dates;
}

function statusLabel(status: TimeRecordStatus): string {
    switch (status) {
        case TimeRecordStatus.CLOCKED_IN:
            return 'Eingestempelt';
        case TimeRecordStatus.CLOCKED_OUT:
            return 'Ausgestempelt';
        case TimeRecordStatus.APPROVED:
            return 'Genehmigt';
        case TimeRecordStatus.SUBMITTED:
            return 'Eingereicht';
        case TimeRecordStatus.LOCKED:
            return 'Gesperrt';
        case TimeRecordStatus.EXPORTED:
            return 'Exportiert';
        case TimeRecordStatus.DRAFT:
            return 'Entwurf';
        case TimeRecordStatus.REJECTED:
            return 'Abgelehnt';
        default:
            return status;
    }
}

function statusColor(
    status: TimeRecordStatus,
): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' {
    switch (status) {
        case TimeRecordStatus.CLOCKED_IN:
            return 'success';
        case TimeRecordStatus.APPROVED:
            return 'success';
        case TimeRecordStatus.SUBMITTED:
            return 'info';
        case TimeRecordStatus.REJECTED:
            return 'error';
        case TimeRecordStatus.LOCKED:
        case TimeRecordStatus.EXPORTED:
            return 'default';
        default:
            return 'default';
    }
}

export function entryColor(type: string): string {
    switch (type) {
        case 'WORK':
            return 'var(--color-success, #22c55e)';
        case 'BREAK':
            return 'var(--color-warning, #f59e0b)';
        case 'ABSENCE':
            return 'var(--color-info, #3b82f6)';
        default:
            return 'var(--color-secondary, #94a3b8)';
    }
}

export function entryTypeLabel(type: string): string {
    switch (type) {
        case 'WORK':
            return 'Arbeit';
        case 'BREAK':
            return 'Pause';
        case 'ABSENCE':
            return 'Abwesenheit';
        default:
            return type;
    }
}

export const mapTimeRecordEntry = (
    entry: TimeRecordEntry
): TimeScaleEntry => ({
    startTime: entry.localStartTime,
    endTime: entry.localEndTime,
    entryType: entry.entryType
});

export const mapTimeRecordEntrySnapshot = (
    entry: TimeRecordEntrySnapshot
): TimeScaleEntry => ({
    startTime: entry.startTimestamp,
    endTime: entry.endTimestamp,
    entryType: entry.entryType
});

const Legend: React.FC = () => (
    <Box display="flex" gap={1.5} alignItems="center" justifyContent="center" flexWrap="wrap">
        {(['WORK', 'BREAK', 'ABSENCE'] as const).map((type) => (
            <Box key={type} display="flex" alignItems="center" gap={0.5}>
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 0.5,
                        bgcolor: entryColor(type),
                        flexShrink: 0,
                    }}
                />
                <Typography variant="caption" color="secondary">
                    {entryTypeLabel(type)}
                </Typography>
            </Box>
        ))}
    </Box>
);

interface DayColumnProps {
    date: Date;
    workday: Workday | undefined;
    onEdit: (isoDate: Date, record: Workday) => void;
}

const WorkdayColumn: React.FC<DayColumnProps> = ({date, workday, onEdit}) => {

    if (!workday) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                gap={1}
                flex={1}
                minWidth={0}
                sx={{px: 1, py: 1, borderRadius: 2}}
            >
                <Box display="flex" flexDirection="column" alignItems="center" gap={1} className="mb-5">
                    <Chip
                        label={DateUtils.formatDate(date, {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                        })}
                        variant="filled"
                        size="small"
                    />
                    <Chip label="–" color="secondary" size="small"/>
                </Box>
            </Box>
        );
    }

    const isCorrection = Boolean(workday.hasCorrection);
    const isWorkingDay = Boolean(workday.workingDay);
    const workedMinutes = workday.actualMinutes ?? 0;
    const targetMinutes = workday.targetMinutes;
    const targetFulfilled = workday.fulfilled;

    const timeRecord = workday.timeRecord;

    // In case time record exists
    const entries = timeRecord?.entries ?? [];
    const isClockedIn =
        timeRecord?.status === TimeRecordStatus.CLOCKED_IN;
    const isTimeRecordEditable = Boolean(
        timeRecord && NON_EDITABLE_STATUSES.includes(timeRecord.status)
    );

    // ---- Derived display data ----
    const normalizedEntries: TimeScaleEntry[] = (() => {
        // Case 1: correction replaces everything
        if (workday?.correction) {
            return workday.correction.entries.map(mapTimeRecordEntrySnapshot);
        }

        // Case 3: normal time record
        return entries.map(mapTimeRecordEntry);
    })();

    const statusText = !isWorkingDay
        ? 'Abwesend'
        : timeRecord
            ? statusLabel(timeRecord.status)
            : 'Offen';

    const canEdit = isWorkingDay && !isTimeRecordEditable;

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={1}
            flex={1}
            minWidth={0}
            sx={{px: 1, py: 1, borderRadius: 2}}
        >
            {/* Header */}
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
                className="mb-5"
            >
                <Chip
                    label={DateUtils.formatDate(date, {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                    })}
                    variant="filled"
                    size="small"
                />

                <Chip
                    label={statusText}
                    color="secondary"
                    size="small"
                />
            </Box>

            {/* Work info */}
            {isWorkingDay && (
                <>
                    <TimeScale
                        entries={normalizedEntries}
                        isClockedIn={isClockedIn}
                        isCorrection={isCorrection}
                    />

                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        gap={0.25}
                    >
                        <Tooltip title={'Bisher erbrachte Arbeitsstunden'} placement="top">
                            <span>
                                <Chip label={formatMinutes(workedMinutes)}
                                      size={'small'}
                                      color={targetFulfilled ? 'success' : 'warning'}
                                      pattern={isCorrection}
                                />
                            </span>
                        </Tooltip>

                        {targetMinutes != null && targetMinutes > 0 && (
                            <>
                                <ArrowRightIcon/>

                                <Tooltip title={'Sollstunden'} placement="top">
                                <span>
                                    <Chip label={formatMinutes(targetMinutes)} size={'small'} color={'secondary'}/>
                                </span>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                </>
            )}

            {/* Edit action */}
            {isWorkingDay && (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={0.25}
                    flex={1}
                >
                    <Tooltip
                        title={isCorrection ? 'Korrektur anpassen oder zurückziehen' : 'Zeiteinträge bearbeiten'}
                        placement="top">
                        <span>
                            <Button
                                variant="outlined"
                                isDisabled={!canEdit}
                                onClick={() => onEdit(date, workday)}
                            >
                                {isCorrection ? 'Korrektur bearbeiten' : 'Bearbeiten'}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

// ── Widget ────────────────────────────────────────────────────────────────────

const WorkDayRecordHistoryWidget: React.FC<DashboardPluginProps> = () => {

    const {t} = useTranslation();

    const {
        workdays,
        windowEnd,
        loading,
        error,
        goOlder,
        goNewer,
        canGoNewer,
        refetch
    } = useWorkdayRecords();

    const {
        creating,
        create,
    } = useRequests<TimeRecordRequest, TimeRecordRequestCreateRequest>({
        fetchRequests: fetchAllTimeRecordRequests,
        createRequest: createTimeRecordRequest,
    });

    const {employee, getEmployee} = useMe();

    useEffect(() => {
        getEmployee();
    }, [getEmployee]);

    const windowDates = useMemo(() => buildWindowDates(windowEnd), [windowEnd]);

    const recordByDate = useMemo(() => {
        const map = new Map<string, Workday>();
        workdays.forEach((r) => map.set(r.workdayDate, r));
        return map;
    }, [workdays]);

    // ── Edit modal state ──────────────────────────────────────────────────────

    const [editTarget, setEditTarget] = useState<{
        date: Date;
        workday: Workday;
    } | null>(null);

    const handleOpenEdit = useCallback(
        (date: Date, record: Workday) => {
            setEditTarget({date: date, workday: record});
        }, [],
    );

    const handleCloseEdit = useCallback(() => setEditTarget(null), []);

    const handleSubmitRequest = useCallback(
        async (data: Omit<TimeRecordRequestCreateRequest, 'employeeId'>) => {
            if (!employee.data?.employeeId) return;
            await create({...data, employeeId: employee.data.employeeId});
            handleCloseEdit();
            // Refresh time records so the updated status (e.g. SUBMITTED) is reflected
            refetch();
        },
        [create, handleCloseEdit, refetch, employee.data],
    );

    useWidgetActions(
        () => (
            <DateRangeNavigator
                windowDates={windowDates}
                onPrevious={goOlder}
                onNext={goNewer}
                canGoNext={canGoNewer}
            />
        ),
        [windowDates, goOlder, goNewer, canGoNewer, t],
    );

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1.5} height="100%">

                {/* Content */}
                {loading ? (
                    <Box display="flex" flex={1} alignItems="center" justifyContent="center">
                        <Spinner/>
                    </Box>

                ) : error ? (
                    <Box display="flex" flex={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2" color="error" align="center">
                            {error.message}
                        </Typography>
                    </Box>

                ) : (
                    <>
                        <Box display="flex" flexDirection="row" gap={0.5} flex={1} alignItems="stretch">
                            {windowDates.map((date, index) => (
                                <React.Fragment key={date.toString()}>
                                    {index > 0 && <Divider orientation="vertical" flexItem/>}
                                    <WorkdayColumn
                                        date={date}
                                        workday={recordByDate.get(DateUtils.toISODate(date))}
                                        onEdit={handleOpenEdit}
                                    />
                                </React.Fragment>
                            ))}
                        </Box>

                        <Legend/>
                    </>
                )}
            </Box>

            {editTarget && (
                <WorkDayRecordEditModal
                    open
                    onClose={handleCloseEdit}
                    date={editTarget.date}
                    workday={editTarget.workday}
                    onSubmit={handleSubmitRequest}
                    submitting={creating}
                />
            )}
        </>
    );
};

export default WorkDayRecordHistoryWidget;